import { FormEvent, useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  api,
  ApiError,
  type Branch,
  type Company,
  type Employee,
  type Location,
  type Mapping,
  type Source,
} from "@/lib/api";

type DimKey = "company" | "branch" | "location" | "employee";

function ResolveForm({
  mapping,
  onDone,
}: {
  mapping: Mapping;
  onDone: () => void;
}) {
  const companies = useQuery<Company[]>({
    queryKey: ["md", "companies"],
    queryFn: api.listCompanies,
  });
  const branches = useQuery<Branch[]>({
    queryKey: ["md", "branches"],
    queryFn: api.listBranches,
  });
  const locations = useQuery<Location[]>({
    queryKey: ["md", "locations"],
    queryFn: api.listLocations,
  });
  const employees = useQuery<Employee[]>({
    queryKey: ["md", "employees"],
    queryFn: api.listEmployees,
  });

  const [dim, setDim] = useState<DimKey>("branch");
  const [targetId, setTargetId] = useState("");
  const [error, setError] = useState<string | null>(null);
  const qc = useQueryClient();

  const options = useMemo(() => {
    if (dim === "company") return companies.data ?? [];
    if (dim === "branch") return branches.data ?? [];
    if (dim === "location") return locations.data ?? [];
    return employees.data ?? [];
  }, [dim, companies.data, branches.data, locations.data, employees.data]);

  const resolve = useMutation({
    mutationFn: () =>
      api.resolveMapping(mapping.id, {
        mapped_dimension: dim,
        mapped_id: targetId,
      }),
    onSuccess: () => {
      setError(null);
      qc.invalidateQueries({ queryKey: ["md", "mappings"] });
      onDone();
    },
    onError: (err) =>
      setError(err instanceof ApiError ? err.detail ?? err.message : String(err)),
  });

  const ignore = useMutation({
    mutationFn: () =>
      api.setMappingStatus(mapping.id, { status: "ignored" }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["md", "mappings"] });
      onDone();
    },
  });

  function submit(e: FormEvent) {
    e.preventDefault();
    if (!targetId) return;
    resolve.mutate();
  }

  return (
    <form onSubmit={submit} className="mt-2 space-y-2">
      <div className="flex gap-2">
        <select
          value={dim}
          onChange={(e) => {
            setDim(e.target.value as DimKey);
            setTargetId("");
          }}
          className="rounded-md border px-2 py-1.5 text-xs bg-white"
        >
          <option value="company">Company</option>
          <option value="branch">Branch</option>
          <option value="location">Location</option>
          <option value="employee">Employee</option>
        </select>
        <select
          value={targetId}
          onChange={(e) => setTargetId(e.target.value)}
          required
          className="rounded-md border px-2 py-1.5 text-xs bg-white flex-1"
        >
          <option value="">Target…</option>
          {options.map((o) => (
            <option key={o.id} value={o.id}>
              {o.name}
            </option>
          ))}
        </select>
        <button
          type="submit"
          disabled={!targetId || resolve.isPending}
          className="bg-brand-600 hover:bg-brand-700 text-white text-xs rounded-md px-3 py-1.5 disabled:opacity-60"
        >
          Resolve
        </button>
        <button
          type="button"
          onClick={() => ignore.mutate()}
          className="bg-slate-200 hover:bg-slate-300 text-slate-800 text-xs rounded-md px-3 py-1.5"
        >
          Ignore
        </button>
      </div>
      {error && <p className="text-xs text-rose-700">{error}</p>}
    </form>
  );
}

function CreateMappingForm({ sources }: { sources: Source[] }) {
  const [source, setSource] = useState("");
  const [entityType, setEntityType] = useState("");
  const [entityId, setEntityId] = useState("");
  const [display, setDisplay] = useState("");
  const [error, setError] = useState<string | null>(null);
  const qc = useQueryClient();

  const create = useMutation({
    mutationFn: () =>
      api.createMapping({
        source_system: source,
        source_entity_type: entityType,
        source_entity_id: entityId,
        source_display_name: display || null,
      }),
    onSuccess: () => {
      setEntityId("");
      setDisplay("");
      setError(null);
      qc.invalidateQueries({ queryKey: ["md", "mappings"] });
    },
    onError: (err) =>
      setError(err instanceof ApiError ? err.detail ?? err.message : String(err)),
  });

  function submit(e: FormEvent) {
    e.preventDefault();
    if (!source || !entityType || !entityId) return;
    create.mutate();
  }

  return (
    <form onSubmit={submit} className="grid grid-cols-1 md:grid-cols-5 gap-3">
      <select
        value={source}
        onChange={(e) => setSource(e.target.value)}
        required
        className="rounded-md border px-3 py-2 text-sm bg-white"
      >
        <option value="">Source system…</option>
        {sources.map((s) => (
          <option key={s.code} value={s.code}>
            {s.name}
          </option>
        ))}
      </select>
      <input
        value={entityType}
        onChange={(e) => setEntityType(e.target.value)}
        placeholder="Entity type (e.g. company)"
        required
        className="rounded-md border px-3 py-2 text-sm"
      />
      <input
        value={entityId}
        onChange={(e) => setEntityId(e.target.value)}
        placeholder="Source entity id"
        required
        className="rounded-md border px-3 py-2 text-sm"
      />
      <input
        value={display}
        onChange={(e) => setDisplay(e.target.value)}
        placeholder="Display name (optional)"
        className="rounded-md border px-3 py-2 text-sm"
      />
      <button
        type="submit"
        disabled={create.isPending || !source || !entityType || !entityId}
        className="bg-brand-600 hover:bg-brand-700 text-white text-sm rounded-md px-3 py-2 disabled:opacity-60"
      >
        Register
      </button>
      {error && (
        <p className="md:col-span-5 text-xs text-rose-700">{error}</p>
      )}
    </form>
  );
}

export default function MappingsPage() {
  const [statusFilter, setStatusFilter] = useState("unmapped");
  const [sourceFilter, setSourceFilter] = useState("");
  const [expandedId, setExpandedId] = useState<string | null>(null);

  const sources = useQuery<Source[]>({
    queryKey: ["md", "sources"],
    queryFn: api.listSources,
  });

  const mappings = useQuery<Mapping[]>({
    queryKey: ["md", "mappings", statusFilter, sourceFilter],
    queryFn: () =>
      api.listMappings({
        status: statusFilter || undefined,
        source_system: sourceFilter || undefined,
      }),
  });

  return (
    <div className="space-y-6">
      <header>
        <h1 className="text-2xl font-semibold text-brand-900">Mappings</h1>
        <p className="text-sm text-slate-600 mt-1">
          Explicit resolution between source-system entities and IRONDash
          dimensions. No mapping is ever inferred silently.
        </p>
      </header>

      <section className="bg-white rounded-lg border shadow-sm p-5">
        <h2 className="font-medium">Register an observation</h2>
        <p className="text-xs text-slate-500 mt-1">
          Connectors will register automatically in later phases; use this
          form to try the flow manually.
        </p>
        {sources.data && (
          <div className="mt-3">
            <CreateMappingForm sources={sources.data} />
          </div>
        )}
      </section>

      <section className="bg-white rounded-lg border shadow-sm p-5">
        <div className="flex items-center justify-between gap-3">
          <h2 className="font-medium">Mappings</h2>
          <div className="flex gap-2 text-sm">
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="rounded-md border px-2 py-1 bg-white"
            >
              <option value="">All</option>
              <option value="unmapped">Unmapped</option>
              <option value="mapped">Mapped</option>
              <option value="ignored">Ignored</option>
            </select>
            <select
              value={sourceFilter}
              onChange={(e) => setSourceFilter(e.target.value)}
              className="rounded-md border px-2 py-1 bg-white"
            >
              <option value="">All sources</option>
              {sources.data?.map((s) => (
                <option key={s.code} value={s.code}>
                  {s.name}
                </option>
              ))}
            </select>
          </div>
        </div>

        {mappings.isLoading && (
          <p className="text-sm text-slate-500 mt-3">Loading…</p>
        )}
        {mappings.data && mappings.data.length === 0 && (
          <p className="text-sm text-slate-500 mt-3">
            {statusFilter === "unmapped"
              ? "No unmapped entities — the queue is empty."
              : "No mappings match the filter."}
          </p>
        )}
        {mappings.data && mappings.data.length > 0 && (
          <table className="mt-3 w-full text-sm">
            <thead className="text-slate-500 border-b">
              <tr>
                <th className="text-left py-2">Source</th>
                <th className="text-left py-2">Entity</th>
                <th className="text-left py-2">Display</th>
                <th className="text-left py-2">Status</th>
                <th className="text-left py-2">Target</th>
              </tr>
            </thead>
            <tbody>
              {mappings.data.map((m) => (
                <tr key={m.id} className="border-b last:border-none align-top">
                  <td className="py-2">{m.source_system}</td>
                  <td className="py-2 font-mono text-xs">
                    {m.source_entity_type}:{m.source_entity_id}
                  </td>
                  <td className="py-2">{m.source_display_name ?? "—"}</td>
                  <td className="py-2">
                    <span
                      className={
                        "inline-flex px-2 py-0.5 rounded-full text-xs border " +
                        (m.status === "mapped"
                          ? "bg-emerald-50 text-emerald-800 border-emerald-200"
                          : m.status === "ignored"
                            ? "bg-slate-100 text-slate-600 border-slate-200"
                            : "bg-amber-50 text-amber-800 border-amber-200")
                      }
                    >
                      {m.status}
                    </span>
                  </td>
                  <td className="py-2 font-mono text-xs">
                    {m.mapped_dimension && m.mapped_id
                      ? `${m.mapped_dimension}:${m.mapped_id.slice(0, 8)}…`
                      : (
                        <button
                          type="button"
                          onClick={() =>
                            setExpandedId(expandedId === m.id ? null : m.id)
                          }
                          className="text-brand-700 hover:underline"
                        >
                          {expandedId === m.id ? "Close" : "Resolve"}
                        </button>
                      )}
                    {expandedId === m.id && (
                      <ResolveForm
                        mapping={m}
                        onDone={() => setExpandedId(null)}
                      />
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>
    </div>
  );
}
